import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TextareaAutosize from 'react-textarea-autosize'
import { Checkbox } from 'semantic-ui-react'

import { IMAGE_SIZES, Shield, Trophy } from 'images'
import { actions, selectors } from 'tournaments'
import { Card, ProgressBar, SimpleButton, Tooltip } from 'ui'

import type { FC, MouseEventHandler } from 'react'

import type { TeamSchema } from 'teams/src'

export interface SearchTeamProps {
  onClose: () => void
  onSelect: (team: TeamSchema) => void
  tournamentId: string

  allowCreation?: boolean
}

const SearchTeam: FC<SearchTeamProps> = ({ allowCreation = false, onClose, onSelect, tournamentId }) => {
  const dispatch = useDispatch()
  const { key, data, loaded, loading, originalAction, pagination } = useSelector(selectors.teams.verifications)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showIncluded, setShowIncluded] = useState(true)
  const [showExcluded, setShowExcluded] = useState(true)
  const [showUnverified, setShowUnverified] = useState(true)
  const [showExistent, setShowExistent] = useState(true)
  const [showNew, setShowNew] = useState(true)
  const [createNew, setCreateNew] = useState(false)
  const [includeTeams, setIncludeTeams] = useState(false)

  useEffect(() => {
    if (key && key !== tournamentId) {
      dispatch(actions.teamsVerificationsClear())
    }
    // clean on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onCleanSearch: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    event.preventDefault()
    setSearch((prev) => {
      const matches =
        prev.match(/\([0-9]+\) (.+)/gim)?.map((match) =>
          match
            .replaceAll(/\([0-9]+\)/gim, '')
            .split('\t')[0]
            ?.trim(),
        ) || []

      return matches.join('\n')
    })
  }
  const onVerifyTeams: MouseEventHandler<HTMLButtonElement> = (event) => {
    event.stopPropagation()
    event.preventDefault()
    dispatch(
      actions.teamsVerifications({
        create: createNew,
        include: includeTeams,
        teams: search,
        tournamentId,
      }),
    )
  }
  const onSelectTeam = (team: TeamSchema) => {
    onSelect(team)
  }

  const originalTeams = (originalAction as { payload?: Record<string, string> })?.payload?.teams || ''

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col flex-wrap gap-2 items-start">
        <div className="w-full">
          <div className="font-bold pb-1">Lista de equipos a verificar</div>
          <div className="text-xs pb-1">Un equipo por línea</div>
          <TextareaAutosize
            className="border bg-greyLightest min-h-20 w-full max-h-80"
            disabled={loading}
            onChange={({ target: { value } }) => setSearch(value)}
            value={search}
          />
          <div className="flex flex-wrap gap-2 pt-1">
            {allowCreation && loaded && originalTeams === search && (
              <Checkbox
                checked={createNew}
                label="Crear equipos nuevos"
                onChange={(_event, { checked }) => setCreateNew(!!checked)}
              />
            )}
            {loaded && originalTeams === search && (
              <Checkbox
                checked={includeTeams}
                label="Incluir equipos en el torneo"
                onChange={(_event, { checked }) => setIncludeTeams(!!checked)}
              />
            )}
          </div>
        </div>
        <div>
          <SimpleButton disabled={loading} icon="recycle" onClick={onCleanSearch} primary>
            Limpiar
          </SimpleButton>
          <SimpleButton disabled={loading} icon="check" onClick={onVerifyTeams} primary>
            Verificar
          </SimpleButton>
          <SimpleButton icon="cancel" onClick={onClose}>
            Cancelar
          </SimpleButton>
        </div>
      </div>
      {loading && (
        <div className="text-sm border-t bg-greyLightest">
          <div className="px-2">Verificando equipos...</div>
          <ProgressBar value={100} />
        </div>
      )}
      {loaded && (
        <div className="flex flex-wrap gap-2 items-center border-t bg-white p-2 pb-0">
          <b>Filtros:</b>
          <Checkbox
            checked={showIncluded}
            label="Incluidos"
            onChange={(_event, { checked }) => setShowIncluded(!!checked)}
          />
          <Checkbox
            checked={showExcluded}
            label="Excluidos"
            onChange={(_event, { checked }) => setShowExcluded(!!checked)}
          />
          <Checkbox
            checked={showUnverified}
            label="Sin Verificar"
            onChange={(_event, { checked }) => setShowUnverified(!!checked)}
          />
          <Checkbox
            checked={showExistent}
            label="Existentes"
            onChange={(_event, { checked }) => setShowExistent(!!checked)}
          />
          <Checkbox checked={showNew} label="Nuevos" onChange={(_event, { checked }) => setShowNew(!!checked)} />
        </div>
      )}
      {loaded && (
        <div className="flex flex-wrap gap-2 items-center border-t bg-white p-2 pb-0 text-sm">
          <span>
            <b>Total:</b> {data.length}
          </span>
          <span>
            <b>Incluidos:</b> {data.filter(({ included }) => included).length}
          </span>
          <span>
            <b>Excluidos:</b> {data.filter(({ excluded }) => excluded).length}
          </span>
          <span>
            <b>Existentes:</b> {data.filter(({ team }) => team).length}
          </span>
          <span>
            <b>Nuevos:</b> {data.filter(({ excluded, included, team }) => !included && !excluded && !team).length}
          </span>
        </div>
      )}
      <div className="m-0 p-2 border-t bg-greyLightest grid grid-cols-1 640:grid-cols-2 768:grid-cols-1 800:grid-cols-2 1280:grid-cols-3 gap-2">
        {data
          .filter(
            ({ excluded, included, team }) =>
              ((showIncluded && included) ||
                (showExcluded && excluded) ||
                (showUnverified && !included && !excluded)) &&
              ((showExistent && team) || (showNew && !team)),
          )
          .map(({ excluded, included, name, team }) => (
            <Card
              key={name}
              className={classnames(
                'flex cursor-pointer p-2 gap-2',
                included && '!border-greenLight',
                excluded && '!border-redLight',
              )}
              elevation={1}
            >
              <div className="flex items-center shrink">
                <Shield className="text-5xl" src={team?.avatar} srcImageSize={IMAGE_SIZES.FIT_100_100} />
              </div>
              <div className="flex flex-col justify-start grow gap-1 overflow-hidden">
                <div className="font-bold truncate">{name}</div>
                {team && (
                  <>
                    <div className="flex justify-between items-end gap-2 text-xs">
                      <div>
                        {team.nickname}
                        <div>
                          {team.relevant_tournaments?.length === 0 && <span>Sin torneos</span>}
                          {team.relevant_tournaments?.length === 1 && (
                            <div key={team.relevant_tournaments[0]?.id} className="flex gap-1 items-center">
                              <Trophy
                                className="text-lg"
                                src={team.relevant_tournaments[0]?.avatar}
                                srcImageSize={IMAGE_SIZES.FIT_45_45}
                              />
                              {team.relevant_tournaments[0]?.name}
                            </div>
                          )}
                          {team.relevant_tournaments?.length > 1 && (
                            <Tooltip
                              tooltip={
                                <div className="flex flex-col gap-1 p-1">
                                  {team.relevant_tournaments.slice(0, 4).map((tournament) => (
                                    <div key={tournament.id} className="flex gap-1 items-center">
                                      <Trophy
                                        className="text-lg"
                                        src={tournament.avatar}
                                        srcImageSize={IMAGE_SIZES.FIT_45_45}
                                      />
                                      {tournament.name}
                                    </div>
                                  ))}
                                  {team.relevant_tournaments.length > 4 && (
                                    <div className="flex gap-1 items-center">
                                      {team.relevant_tournaments.length - 4} más
                                    </div>
                                  )}
                                </div>
                              }
                            >
                              <span>{team.relevant_tournaments.length} torneos</span>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </div>
                    {!included && !excluded && (
                      <div className="flex justify-end text-sm">
                        <Tooltip tooltip="Agregar este equipo al torneo">
                          <SimpleButton icon="plus" onClick={() => onSelectTeam(team)} tertiary value={team.id}>
                            Seleccionar
                          </SimpleButton>
                        </Tooltip>
                      </div>
                    )}
                    <div className="flex justify-end gap-2 text-xs">
                      {team.discipline?.name && (
                        <div className="px-2 rounded-md border border-current text-blue truncate">
                          {team.discipline.name}
                        </div>
                      )}
                      {team.gender?.name && (
                        <div className="px-2 rounded-md border border-current text-green truncate">
                          {team.gender.name}
                        </div>
                      )}
                      {team.category?.name && (
                        <div className="px-2 rounded-md border border-current text-orange truncate">
                          {team.category.name}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </Card>
          ))}
      </div>
      {pagination && search.trim().length >= 3 && (
        <div className="text-sm flex flex-wrap  items-center justify-end gap-1">
          <span>
            Página {pagination.current_page} de {pagination.last_page}
          </span>
          {page > 1 && (
            <SimpleButton className="text-sm" onClick={() => setPage(page - 1)}>
              Anterior
            </SimpleButton>
          )}
          {page < pagination.last_page && <SimpleButton onClick={() => setPage(page + 1)}>Siguiente</SimpleButton>}
        </div>
      )}
    </div>
  )
}

export default SearchTeam
